// Text templates shared by the Worker and the web app. Plain text only, no PHI,
// no SSNs: only identifiers payers already use (NPI, CAQH ID, payer reference no.).

export interface FollowUpContext {
  providerName: string;
  providerNpi?: string | null;
  caqhId?: string | null;
  payerName: string;
  payerReferenceNo?: string | null;
  submittedOn?: string | null; // YYYY-MM-DD
  entityName?: string | null;
  groupNpi?: string | null;
  tinLast4?: string | null;
  daysSinceSubmitted?: number | null;
  senderName: string;
  orgName: string;
  backdateRequestedTo?: string | null;
}

const fmtDate = (iso?: string | null) =>
  iso ? new Date(`${iso}T12:00:00Z`).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric", timeZone: "UTC" }) : null;

/** Draft of a payer follow-up email. A human reviews, edits and sends it from their own mailbox. */
export function followUpDraft(c: FollowUpContext): { subject: string; body: string } {
  const ref = c.payerReferenceNo ? ` (Ref# ${c.payerReferenceNo})` : "";
  const subject = `Credentialing status request: ${c.providerName}${c.providerNpi ? `, NPI ${c.providerNpi}` : ""}${ref}`;
  const lines = [
    "Hello,",
    "",
    `I am following up on the credentialing application for the provider below${
      c.submittedOn ? `, submitted on ${fmtDate(c.submittedOn)}` : ""
    }${c.daysSinceSubmitted ? ` (${c.daysSinceSubmitted} days ago)` : ""}.`,
    "",
    `  Provider: ${c.providerName}, BCBA`,
    c.providerNpi ? `  Individual NPI: ${c.providerNpi}` : null,
    c.caqhId ? `  CAQH ProView ID: ${c.caqhId}` : null,
    c.entityName ? `  Group: ${c.entityName}` : null,
    c.groupNpi ? `  Group NPI: ${c.groupNpi}` : null,
    c.tinLast4 ? `  Group TIN (last 4): ${c.tinLast4}` : null,
    c.payerReferenceNo ? `  Application/reference #: ${c.payerReferenceNo}` : null,
    "",
    "Could you please let us know:",
    "  1. The current status of the application;",
    "  2. Whether any information or documents are outstanding;",
    "  3. The expected decision date and the effective date that will be assigned.",
    c.backdateRequestedTo
      ? `  4. Whether our request for an effective date of ${fmtDate(c.backdateRequestedTo)} can be honored.`
      : null,
    "",
    "Thank you,",
    c.senderName,
    c.orgName,
  ];
  return { subject, body: lines.filter((l) => l !== null).join("\n") };
}

export function mailtoHref(to: string | null | undefined, subject: string, body: string): string {
  return `mailto:${encodeURIComponent(to ?? "")}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
}

export function signInEmail(opts: { link: string; orgName: string; isProvider: boolean; firstName?: string | null; code?: string | null }) {
  const hello = opts.firstName ? `Hi ${opts.firstName},` : "Hello,";
  const purpose = opts.isProvider
    ? `${opts.orgName} uses this secure portal to collect your credentialing documents (license, BACB certificate, malpractice policy, resume, NPI and CAQH ID).`
    : `You have access to the ${opts.orgName} credentialing tracker.`;
  const text = [
    hello,
    "",
    purpose,
    "",
    ...(opts.code ? [`Your sign-in code: ${opts.code}`, "", "Enter it on the sign-in page, or use this link instead:"] : ["Use this link to sign in:"]),
    opts.link,
    "",
    "The code and link work once and expire in 1 hour. If you did not expect this email, you can ignore it.",
  ].join("\n");
  return { subject: opts.code ? `${opts.code} is your ${opts.orgName} sign-in code` : `${opts.orgName}: your sign-in link`, text };
}
